import { defineStore } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';
import type { WordOfTheDayItem, KeywordSearchResult } from '@ilteducation/word-of-the-day-types';
import { useConfigHelper } from '@ilteducation/config';
import axios from 'axios';
import { useGameEvents } from '@ilteducation/activity-stream-events';
import { useClientAnalytics } from '@ilteducation/client-analytics';
import { v4 as uuidv4 } from 'uuid';
import { useMyOrganizations } from '@ilteducation/my-account';
import type { SlimOrganization } from '@ilteducation/my-account-types';
import { useUserContext } from '@ilteducation/ilt-auth';
import { buildWordContext, keywordResultToWordItem, todayIso } from '../utils';

export type WordContext = { description?: string; imageUrl?: string };
export type WordSource = 'global' | 'custom';
type WotdEventMetadata = { wordSource: WordSource; win?: boolean; incorrectGuesses?: number };

const useWordOfTheDayStore = defineStore('games-word-of-the-day', () => {
  const gameState = ref<'init' | 'game' | 'game-over' | 'results'>('init');
  const keywordOfTheDay = ref<WordOfTheDayItem>();
  const globalWord = ref<WordOfTheDayItem>();
  const wordSource = ref<WordSource>('global');
  const word = ref('');
  const selectedLetters = ref<Set<string>>(new Set());
  const correctLetters = ref<string[]>([]);
  const wordContext = ref<WordContext>();
  const guesses = computed(() => selectedLetters.value.size - correctLetters.value.length);
  const guessedCorrectWord = computed(() => correctLetters.value.length === new Set(word.value.split('')).size);
  const { getApiUrl } = useConfigHelper();
  const cdnDomain = process.env.VITE_CDN_DOMAIN;
  const sessionId = ref<string>(uuidv4()); // Generate a random session ID
  const { user, roles } = useUserContext();
  const { gameStarted, gameEnded } = useGameEvents({ game: 'word-of-the-day' });
  const { sendAnalyticsEvent } = useClientAnalytics();
  const { organizations } = useMyOrganizations(true);

  const sendGameStartEvent = () => {
    gameStarted(
      sessionId.value,
      user.uid,
      roles.map((r) => r.value),
      organizations.value.data.map((org: SlimOrganization) => org.uid),
    );
    sendAnalyticsEvent('AppUserEventType', {
      eventName: 'word_of_the_day_started',
      metadata: { wordSource: wordSource.value } satisfies WotdEventMetadata,
    });
  };

  const sendGameEndEvent = () => {
    gameEnded({
      game: 'word-of-the-day',
      incorrectGuesses: guesses.value,
      win: guessedCorrectWord.value,
    });
    sendAnalyticsEvent('AppUserEventType', {
      eventName: 'word_of_the_day_ended',
      metadata: {
        wordSource: wordSource.value,
        win: guessedCorrectWord.value,
        incorrectGuesses: guesses.value,
      } satisfies WotdEventMetadata,
    });
  };

  const getKeywordOfTheDay = async (): Promise<WordOfTheDayItem> => {
    const kw = await axios.get(`${getApiUrl('word-of-the-day', true)}/word`);
    return kw.data;
  };

  /** Makes `item` the word to play, resetting any in-progress guesses. */
  const applyWord = (item: WordOfTheDayItem, source: WordSource) => {
    keywordOfTheDay.value = item;
    wordContext.value = buildWordContext(item, cdnDomain);
    word.value = item.keyword;
    wordSource.value = source;
    selectedLetters.value = new Set();
    correctLetters.value = [];
  };

  /** Play a user-picked keyword as today's word. */
  const selectCustomWord = (result: KeywordSearchResult) => {
    applyWord(keywordResultToWordItem(result, todayIso()), 'custom');
  };

  /** Fall back to today's global word. */
  const useTodaysGlobalWord = () => {
    if (globalWord.value) {
      applyWord(globalWord.value, 'global');
    }
  };

  const setGameState = (state: 'init' | 'game' | 'results' | 'game-over') => {
    gameState.value = state;
  };

  const getWordProgress = computed(() => {
    return word.value
      .split('')
      .map((letter) => (selectedLetters.value.has(letter) ? letter : ' '))
      .join('');
  });

  const makeGuess = (letter: string) => {
    // eslint-disable-next-line no-param-reassign
    letter = letter.toLowerCase();
    selectedLetters.value.add(letter);
    if (word.value.includes(letter)) {
      if (!correctLetters.value.includes(letter)) {
        correctLetters.value.push(letter);
      }
    }
    if (guesses.value === 10 || correctLetters.value.length === new Set(word.value.split('')).size) {
      setGameState('game-over'); // End game if 10 guesses are made
    }
  };
  const clear = () => {
    gameState.value = 'init';
    selectedLetters.value = new Set();
    correctLetters.value = [];
    useTodaysGlobalWord();
  };

  onMounted(async () => {
    globalWord.value = await getKeywordOfTheDay();
    applyWord(globalWord.value, 'global');
  });

  watch(gameState, (gs) => {
    if (gs === 'game-over') {
      sendGameEndEvent();
      sessionId.value = uuidv4(); // Reset session ID for the next game
    }
  });

  return {
    gameState,
    word,
    wordContext,
    selectedLetters,
    setGameState,
    getWordProgress,
    makeGuess,
    clear,
    guesses,
    guessedCorrectWord,
    keywordOfTheDay,
    wordSource,
    selectCustomWord,
    useTodaysGlobalWord,
    sendGameStartEvent,
    sendGameEndEvent,
  };
});

export default useWordOfTheDayStore;
